import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { slugify } from "./slugify";
import { IMAGE_COMING_SOON } from "./imageComingSoon";
import "./FeaturedProducts.css";


const FIELD_PATTERNS = {
  title: [/^product.?name$/i, /^tit+le$/i, /^name$/i],
  partNo: [/part.?no/i, /part.?number/i, /model.?no/i, /biba.?part/i, /^sku$/i],
  price: [/^price$/i, /selling.?price/i, /^mrp$/i, /^rate$/i],
  stock: [/^stock$/i, /^qty$/i, /quantity/i, /stock.?(qty|quantity|count)/i, /availability/i],
};

const testKey = (key, patterns) => patterns.some((p) => p.test(key.trim()));

const matchField = (fieldsObj, patterns) => {
  const entry = Object.entries(fieldsObj || {}).find(([key]) => testKey(key, patterns));
  return entry ? entry[1] ?? "" : "";
};

// Shared ambient background: solid color blobs glowing from the
// section's corners, plus a soft overhead light wash, behind the
// plain transparent glass cards. Present in every state
// (loading/error/success) for a consistent look.
const AmbientBackground = () => (
  <>
    <div className="featured-light-wash" aria-hidden="true" />
    <div className="featured-blob-field" aria-hidden="true">
      <div className="featured-blob featured-blob-1" />
      <div className="featured-blob featured-blob-2" />
      <div className="featured-blob featured-blob-3" />
      <div className="featured-blob featured-blob-4" />
      <div className="featured-blob featured-blob-5" />
    </div>
  </>
);

// Cycled per card so each one gets its own subtle corner color splash
// (see --fp-accent in FeaturedProducts.css) instead of every card
// looking identical.
const ACCENT_PALETTE = ["#ff8a3d", "#d946ef", "#3b82f6", "#f2c879", "#34d399"];

const FeaturedProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const gridRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isCancelled = false;

    const fetchFeaturedProducts = async () => {
      try {
        const allFeatured = [];
        // A product linked to more than one category in tixaru is synced
        // under each of them (so it shows on both listing pages), but the
        // featured carousel must show it only once. Doc ids are the ERP
        // product id, identical in every copy; part number is a second
        // guard against distinct docs for the same physical part.
        const seenFeatured = new Set();

        // Fetch all categories
        const categoriesRef = collection(db, "product_categories");
        const categoriesSnap = await getDocs(categoriesRef);

        // For each category, fetch subCategories and products
        for (const categoryDoc of categoriesSnap.docs) {
          const categoryId = categoryDoc.id;
          const categoryName = categoryDoc.data().categoryName || "";
          const categorySlug = slugify(categoryName);

          // Fetch subCategories
          const subCategoriesRef = collection(
            db,
            "product_categories",
            categoryId,
            "subCategories"
          );
          const subCategoriesSnap = await getDocs(subCategoriesRef);

          // For each subCategory, fetch productData
          for (const subCatDoc of subCategoriesSnap.docs) {
            const subCatId = subCatDoc.id;
            const subCategoryName = subCatDoc.data()?.subCategoryName || subCatId;
            const subCategorySlug = slugify(subCategoryName);

            const productsRef = collection(
              db,
              "product_categories",
              categoryId,
              "subCategories",
              subCatId,
              "productData"
            );
            const productsSnap = await getDocs(productsRef);

            const usedTitleSlugs = new Set();

            productsSnap.docs.forEach((productDoc) => {
              const raw = productDoc.data() || {};
              const nested =
                raw.fields && typeof raw.fields === "object" ? raw.fields : null;
              const fields = nested ? { ...raw, ...nested } : raw;

              const rawTitle = matchField(fields, FIELD_PATTERNS.title) || subCategoryName;
              let titleSlug = slugify(rawTitle);
              if (usedTitleSlugs.has(titleSlug)) {
                titleSlug = `${titleSlug}-${productDoc.id.slice(0, 6).toLowerCase()}`;
              }
              usedTitleSlugs.add(titleSlug);

              if (fields.isFeatured !== true) return;

              const partNo =
                matchField(fields, FIELD_PATTERNS.partNo) || fields.productId || "";
              const dedupeKeys = [productDoc.id, partNo].filter(Boolean);
              if (dedupeKeys.some((k) => seenFeatured.has(k))) return;
              dedupeKeys.forEach((k) => seenFeatured.add(k));

              allFeatured.push({
                id: productDoc.id,
                categorySlug,
                categoryId,
                categoryName,
                subCatId,
                subCategorySlug,
                partNoSlug: titleSlug,
                ...fields,
                title: rawTitle,
                partNo,
                price:
                  matchField(fields, FIELD_PATTERNS.price) ||
                  fields.priceTiers?.[0]?.price ||
                  "",
                stock: matchField(fields, FIELD_PATTERNS.stock) || "",
              });
            });
          }
        }

        if (!isCancelled) {
          setProducts(allFeatured);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching featured products:", err);
        if (!isCancelled) {
          setError("Failed to load featured products. Please try again later.");
          setLoading(false);
        }
      }
    };

    fetchFeaturedProducts();
    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    const updateMaxScroll = () => {
      if (gridRef.current) {
        const { scrollWidth, clientWidth } = gridRef.current;
        setMaxScroll(Math.max(0, scrollWidth - clientWidth));
      }
    };

    updateMaxScroll();
    window.addEventListener("resize", updateMaxScroll);
    return () => window.removeEventListener("resize", updateMaxScroll);
  }, [products]);

  const handleScroll = (direction) => {
    if (!gridRef.current) return;

    const scrollAmount = gridRef.current.clientWidth;
    const newPosition =
      direction === "next"
        ? Math.min(scrollPosition + scrollAmount, maxScroll)
        : Math.max(scrollPosition - scrollAmount, 0);

    gridRef.current.scrollTo({
      left: newPosition,
      behavior: "smooth",
    });

    setScrollPosition(newPosition);
  };

  const handleGridScroll = () => {
    if (gridRef.current) {
      setScrollPosition(gridRef.current.scrollLeft);
    }
  };

  if (loading) {
    return (
      <section className="featured-section">
        <AmbientBackground />
        <div className="featured-container">
          <div className="featured-skeleton-grid" aria-label="Loading featured products">
            {Array.from({ length: 5 }).map((_, i) => (
              <motion.div
                key={i}
                className="featured-skeleton-card"
                initial={{ opacity: 0, y: -40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  type: "spring",
                  bounce: 0.5,
                  duration: 0.7,
                  delay: i * 0.09,
                }}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="featured-section">
        <AmbientBackground />
        <div className="featured-container">
          <div className="featured-error">
            <p>⚠️ Error Loading Featured Products</p>
            <p>{error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return (
      <section className="featured-section">
        <AmbientBackground />
        <div className="featured-container">
          <div className="featured-error">
            <p>No Featured Products</p>
            <p>There are currently no featured products to display.</p>
          </div>
        </div>
      </section>
    );
  }

  const canScrollPrev = scrollPosition > 0;
  const canScrollNext = scrollPosition < maxScroll;

  return (
    <section className="featured-section">
      <AmbientBackground />
      <div className="featured-container">

        <div className="featured-heading-wrap">
          <h2 className="featured-heading">
            Featured <span>Products</span>
          </h2>
          <div className="featured-heading-ornament">
            <div className="featured-orn-line-l"></div>
            <div className="featured-orn-gem"></div>
            <div className="featured-orn-line-r"></div>
          </div>
        </div>

        <div className="featured-carousel-wrapper">

          <div
            className="featured-grid"
            ref={gridRef}
            onScroll={handleGridScroll}
          >
            {products.map((product, index) => {
              // Tixaru's own Front/Back Image Url fields only (the literal
              // keys catalogSync.js writes) -- no gallery/other fallback,
              // same policy as the rest of the site's product images.
              const imageUrl =
                product["Front Image Url"] || product["Back Image Url"] || IMAGE_COMING_SOON;

              const title = product.title || "Untitled Product";
              const accent = ACCENT_PALETTE[index % ACCENT_PALETTE.length];

              return (
                <motion.div
                  key={product.id}
                  className="featured-grid-item"
                  initial={{ opacity: 0, y: -60, scale: 0.85 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    type: "spring",
                    bounce: 0.45,
                    duration: 0.7,
                    delay: index * 0.08,
                  }}
                >
                  <div
                    className="featured-card"
                    style={{ "--fp-accent": accent }}
                    onClick={() =>
                      navigate(
                        `/tft/${product.categorySlug}/${product.subCategorySlug}/${product.partNoSlug}`
                      )
                    }
                  >
                    <div className="featured-card-top">
                      {product.partNo && (
                        <span className="featured-part-badge">{product.partNo}</span>
                      )}
                      <img
                        src={imageUrl}
                        alt={title}
                        className="featured-prod-image"
                        onError={(e) => (e.target.src = IMAGE_COMING_SOON)}
                      />
                    </div>

                    <div className="featured-card-bottom">
                      <h3 className="featured-card-title">{title}</h3>
                      <div className="featured-card-divider" />
                      <div className="featured-card-cta">
                        <span className="featured-card-cta-text">View Details</span>
                        <span className="featured-card-cta-arrow">→</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="featured-carousel-controls">
            <button
              className="featured-carousel-btn featured-carousel-prev"
              onClick={() => handleScroll("prev")}
              disabled={!canScrollPrev}
              aria-label="Previous products"
            >
              ‹
            </button>
            <button
              className="featured-carousel-btn featured-carousel-next"
              onClick={() => handleScroll("next")}
              disabled={!canScrollNext}
              aria-label="Next products"
            >
              ›
            </button>
          </div>
        </div>

        <div className="featured-carousel-indicators">
          {/* <span className="featured-indicator-text">
            Showing{" "}
            <strong>
              {Math.min(products.length, 4)} of {products.length}
            </strong>{" "}
            Featured
          </span> */}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;